/**
 * Installer detail page (standalone, matches Support Board's list→detail pattern).
 *
 * URL: `/staff/installers/:userId`
 *
 * Fetches everything needed to admin a single installer:
 *   - Basic profile (name, company, email, phone, joined date)
 *   - Section access toggles (ProBound / ProEpoxy / ProDecor)
 *   - Live stats (leads, quotes)
 *   - Recent leads and quotes with clickthrough
 *
 * Kept as a proper page (not a modal) so back/forward, deep-links from the
 * SMS notifications, and browser bookmarks all Just Work.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Phone, Calendar, MessageSquare, MessageCircle, Plus,
  ChevronRight, Loader2, PoundSterling, Trash2, AlertTriangle, MapPin,
} from 'lucide-react';

import ProPortalHeader from '@/components/ProPortalHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ApprovedBadgeList } from '@/components/certifications/ApprovedBadge';
import SendMessageModal from '@/components/messaging/SendMessageModal';
import { SendTemplateEmailModal } from '@/components/email/SendTemplateEmailModal';
import MessageThreadModal from '@/components/messaging/MessageThreadModal';
import { useConfirm } from '@/components/ConfirmDialog';
import { StaffTierCard } from '@/components/staff/StaffTierCard';
import AuthContext from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'probound', label: 'ProBound',  sub: 'Resin Bound Surfacing', color: '#2F5E96' },
  { id: 'proepoxy', label: 'ProEpoxy',  sub: 'Epoxy Floor Systems',    color: '#7A0B7A' },
  { id: 'prodecor', label: 'ProDecor',  sub: 'Decorative Walling',     color: '#D5AB09' },
  { id: 'protraining', label: 'ProTraining', sub: 'Course notes, videos, documents', color: '#1f4030' },
];

const SERVICE_COLORS = {
  probound: 'bg-blue-100 text-blue-800',
  proepoxy: 'bg-purple-100 text-purple-800',
  prodecor: 'bg-amber-100 text-amber-800',
};

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

export default function InstallerDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useContext(AuthContext) || {};
  // Admin or manager can delete; matches the backend guard on
  // `DELETE /api/staff/users/{id}`. Managers can only delete accounts they
  // themselves created — the backend enforces that; the button is still
  // rendered so managers see it exists and get a clean 403 explanation
  // if they don't own the account.
  const canDelete = ['admin', 'manager'].includes(user?.role);
  const [deleting, setDeleting] = useState(false);

  const [details, setDetails] = useState(null); // { installer, lead_count, quote_count, leads, quotes }
  const [certs, setCerts] = useState([]);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageTarget, setMessageTarget] = useState(null);
  const [threadOpen, setThreadOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, cRes, tRes] = await Promise.all([
        fetch(`${API_URL}/api/staff/installers/${userId}`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/certifications`, { credentials: 'include', headers: authHeaders() }).catch(() => null),
        fetch(`${API_URL}/api/admin/pricing-tiers`, { credentials: 'include', headers: authHeaders() }).catch(() => null),
      ]);
      if (!dRes.ok) throw new Error(`HTTP ${dRes.status}`);
      setDetails(await dRes.json());
      if (cRes && cRes.ok) {
        const list = await cRes.json();
        setCerts((list || []).filter((c) => c.user_id === userId));
      }
      if (tRes && tRes.ok) setPricingTiers(await tRes.json());
    } catch (e) {
      toast.error('Could not load installer', { description: String(e).slice(0, 200) });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const updateAccess = async (newAccess) => {
    if (!details?.installer) return;
    // Optimistic — flip the checkbox instantly, roll back on failure.
    const prev = details.installer.access || {};
    setDetails((d) => ({ ...d, installer: { ...d.installer, access: newAccess } }));
    try {
      const r = await fetch(`${API_URL}/api/staff/installers/${userId}/access`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ access: newAccess }),
      });
      if (!r.ok) throw new Error('Failed to update access');
      toast.success('Section access updated');
    } catch (e) {
      setDetails((d) => ({ ...d, installer: { ...d.installer, access: prev } }));
      toast.error(e.message);
    }
  };

  const setTier = async (tierId) => {
    if (!details?.installer) return;
    const prev = details.installer.pricing_tier_id;
    setDetails((d) => ({ ...d, installer: { ...d.installer, pricing_tier_id: tierId } }));
    try {
      const r = await fetch(`${API_URL}/api/admin/users/${userId}/pricing-tier`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ pricing_tier_id: tierId }),
      });
      if (!r.ok) throw new Error('Failed to set tier');
      const label = pricingTiers.find((t) => t.tier_id === tierId)?.label || 'tier';
      toast.success(`Moved to ${label}`);
    } catch (e) {
      setDetails((d) => ({ ...d, installer: { ...d.installer, pricing_tier_id: prev } }));
      toast.error(e.message);
    }
  };

  const deleteInstaller = async () => {
    if (!details?.installer || !canDelete) return;
    const inst = details.installer;
    const ok = await confirm({
      title: `Delete ${inst.name}?`,
      body: `This removes the installer's login and hides them from every list.\n\nEmail: ${inst.email}\n\nYou can restore them within 30 days from Manage Installers → Recently deleted.`,
      confirmLabel: 'Delete installer',
      tone: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/users/${userId}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `Failed (${r.status})`);
      toast.success(`Deleted ${inst.name}`);
      navigate('/staff/installers');
    } catch (e) {
      toast.error(String(e.message || e).slice(0, 200));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="installer-detail-loading">
        <ProPortalHeader activeSection="staff" />
        <div className="max-w-5xl mx-auto p-6 flex items-center justify-center min-h-[40vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#7A0B7A]" />
        </div>
      </div>
    );
  }

  if (!details?.installer) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProPortalHeader activeSection="staff" />
        <div className="max-w-5xl mx-auto p-6">
          <button onClick={() => navigate('/staff/installers')} className="text-sm text-[#7A0B7A] hover:underline mb-4">
            ← Back to installers
          </button>
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500">
            Installer not found.
          </div>
        </div>
      </div>
    );
  }

  const inst = details.installer;
  const currentTierLabel = pricingTiers.find((t) => t.tier_id === inst.pricing_tier_id)?.label;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="installer-detail-page">
      <ProPortalHeader activeSection="staff" />

      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/staff/installers')}
            className="p-2 hover:bg-gray-200 rounded-lg"
            data-testid="installer-detail-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{inst.name}</h1>
              {currentTierLabel && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200">
                  {currentTierLabel}
                </span>
              )}
            </div>
            {inst.company_name && <p className="text-sm text-gray-500">{inst.company_name}</p>}
            {certs.length > 0 && <div className="mt-1.5"><ApprovedBadgeList certs={certs} /></div>}
          </div>
        </div>

        {/* Contact + quick actions */}
        <Card className="mb-4">
          <CardContent className="p-4 flex flex-col md:flex-row gap-4">
            <div className="flex-1 space-y-2 text-sm text-gray-700">
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" /> {inst.email}</div>
              {inst.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" /> {inst.phone}</div>}
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" /> Joined {new Date(inst.created_at).toLocaleDateString()}</div>
            </div>
            <div className="flex flex-wrap gap-2 items-start">
              <Button variant="outline" size="sm"
                className="border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                disabled={!inst.phone || inst.sms_opt_in === false}
                onClick={() => setMessageTarget({ channel: 'sms' })}
                data-testid="detail-sms-btn"
              >
                <MessageSquare className="w-4 h-4 mr-1" /> SMS
              </Button>
              <Button variant="outline" size="sm"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                disabled={!inst.phone || inst.whatsapp_opt_in === false}
                onClick={() => setMessageTarget({ channel: 'whatsapp' })}
                data-testid="detail-wa-btn"
              >
                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
              </Button>
              <Button variant="outline" size="sm"
                className="border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-40"
                disabled={!inst.email}
                onClick={() => setEmailOpen(true)}
                data-testid="detail-email-btn"
              >
                <Mail className="w-4 h-4 mr-1" /> Email
              </Button>
              <Button variant="outline" size="sm" onClick={() => setThreadOpen(true)} data-testid="detail-thread-btn">
                Thread
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/calendar?installer=${userId}`)} data-testid="detail-calendar-btn">
                <Calendar className="w-4 h-4 mr-1" /> Calendar
              </Button>
              <Button size="sm" className="bg-[#7A0B7A] hover:bg-[#5a085a]"
                onClick={() => navigate(`/leads?installer=${userId}&action=add`)}
                data-testid="detail-add-lead-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Lead
              </Button>
            </div>
          </CardContent>
        </Card>

        <CoverageCard installer={inst} />

        <div className="mb-4"><StaffTierCard userId={userId} /></div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#7A0B7A]" data-testid="detail-lead-count">{details.lead_count || 0}</p>
              <p className="text-sm text-gray-500">Total Leads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#2F5E96]" data-testid="detail-quote-count">{details.quote_count || 0}</p>
              <p className="text-sm text-gray-500">Total Quotes</p>
            </CardContent>
          </Card>
        </div>

        {/* Section access */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <h3 className="font-bold text-gray-900 mb-1">Section Access</h3>
            <p className="text-xs text-gray-500 mb-3">Enable / disable which sections this installer can see and quote against.</p>
            <div className="space-y-2">
              {SECTIONS.map((s) => (
                <label key={s.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                  data-testid={`detail-access-${s.id}-label`}
                >
                  <input
                    type="checkbox"
                    checked={inst.access?.[s.id] || false}
                    onChange={(e) => updateAccess({ ...inst.access, [s.id]: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300"
                    style={{ accentColor: s.color }}
                    data-testid={`detail-access-${s.id}`}
                  />
                  <div className="flex-1">
                    <span className="font-semibold" style={{ color: s.color }}>{s.label}</span>
                    <p className="text-xs text-gray-500">{s.sub}</p>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pricing tier */}
        {pricingTiers.length > 0 && (
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <PoundSterling className="w-4 h-4 text-emerald-700" />
                <h3 className="font-bold text-gray-900">Pricing tier</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">Controls which customer-facing quote prices this installer sees.</p>
              <select
                value={inst.pricing_tier_id || ''}
                onChange={(e) => setTier(e.target.value)}
                className="w-full sm:w-auto text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-emerald-500"
                data-testid="detail-tier-select"
              >
                {!inst.pricing_tier_id && <option value="">— unassigned —</option>}
                {pricingTiers.map((t) => (
                  <option key={t.tier_id} value={t.tier_id}>{t.label}{t.is_default ? ' (default)' : ''}</option>
                ))}
              </select>
            </CardContent>
          </Card>
        )}

        {/* Recent leads */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900">Recent leads</h3>
              <Button size="sm" className="bg-[#7A0B7A] hover:bg-[#5a085a]"
                onClick={() => navigate(`/leads?installer=${userId}&action=add`)}>
                <Plus className="w-4 h-4 mr-1" /> Add Lead
              </Button>
            </div>
            {(details.leads || []).length === 0 ? (
              <p className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">No leads yet.</p>
            ) : (
              <div className="space-y-2">
                {details.leads.slice(0, 8).map((lead) => (
                  <button
                    key={lead.id}
                    onClick={() => navigate(`/leads?leadId=${lead.id}`)}
                    className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-between transition"
                    data-testid={`detail-lead-${lead.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium truncate">{lead.customer_name}</p>
                      <div className="flex gap-1.5 mt-1 flex-wrap">
                        {lead.service_type && (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${SERVICE_COLORS[lead.service_type] || 'bg-gray-100 text-gray-700'}`}>
                            {lead.service_type}
                          </span>
                        )}
                        {lead.status && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-white border border-gray-300 text-gray-700">
                            {lead.status}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent quotes */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-bold text-gray-900 mb-3">Recent quotes</h3>
            {(details.quotes || []).length === 0 ? (
              <p className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">No quotes yet.</p>
            ) : (
              <div className="space-y-2">
                {details.quotes.slice(0, 8).map((q) => (
                  <div key={q.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{q.reference || q.name || q.customer_name || 'Untitled Quote'}</p>
                      <p className="text-xs text-gray-500">
                        {q.created_at ? new Date(q.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <p className="font-bold text-[#7A0B7A]">
                      £{Number(q.pricing_snapshot?.total_inc_vat ?? q.total ?? q.total_price ?? 0).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        {/* Danger Zone — admin / manager only. Backend also enforces
            role + ownership on `DELETE /api/staff/users/{id}`; the button is
            just the discoverable UI for it. */}
        {canDelete && (
          <Card className="mt-4 border-red-200 bg-red-50/40" data-testid="detail-danger-zone">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-red-900">Danger zone</h3>
                  <p className="text-xs text-red-800/80 mt-0.5">
                    Deleting hides this installer from every list and stops them logging in. You have <strong>30 days</strong> to restore them from Manage Installers → Recently deleted before they're purged permanently.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-red-400 text-red-700 hover:bg-red-100 shrink-0"
                  onClick={deleteInstaller}
                  disabled={deleting}
                  data-testid="detail-delete-installer"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {deleting ? 'Deleting…' : 'Delete installer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <SendMessageModal
        open={Boolean(messageTarget)}
        onClose={() => setMessageTarget(null)}
        channel={messageTarget?.channel || 'sms'}
        recipientName={inst.name}
        recipientPhone={inst.phone}
        recipientRef={inst.user_id}
      />
      <SendTemplateEmailModal open={emailOpen} onClose={() => setEmailOpen(false)} installer={inst} />
      <MessageThreadModal
        open={threadOpen}
        onClose={() => setThreadOpen(false)}
        installerId={inst.user_id}
        installerName={inst.name}
      />
    </div>
  );
}


/**
 * Coverage card — shows the installer's saved lead territories grouped
 * by region. Silent when the installer hasn't picked any coverage yet
 * (they'd still be onboarding). Renders inline chips per region with a
 * counter so staff can scan multiple regions at a glance without
 * having to click into each one.
 *
 * Data source: `installer.lead_territories` (flat array) +
 * `installer.lead_regions_covered` (derived on save). We regroup
 * client-side to keep this component decoupled from the backend
 * catalogue — a rename of one territory later doesn't strand the UI.
 */
function CoverageCard({ installer }) {
  const territories = Array.isArray(installer.lead_territories) ? installer.lead_territories : [];
  const [expanded, setExpanded] = useState(false);
  const [catalogue, setCatalogue] = useState(null);

  useEffect(() => {
    if (territories.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/installer/lead-areas`, {
          credentials: 'include',
          headers: authHeaders(),
        });
        if (!res.ok) return; // Staff endpoints for installer's own catalogue don't exist — this call reflects the caller's picks, but we only need the region tree, which is invariant.
        const data = await res.json();
        if (!cancelled) setCatalogue(data.catalogue || []);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [territories.length]);

  if (territories.length === 0) {
    return (
      <Card className="mb-4" data-testid="coverage-card-empty">
        <CardContent className="p-4 flex items-center gap-3 text-sm text-gray-500">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span>No coverage area set yet — installer hasn’t picked which UK territories they’ll travel to.</span>
        </CardContent>
      </Card>
    );
  }

  // Group flat territories → regions using the catalogue when available;
  // fall back to "Uncategorised" if the catalogue hasn't loaded yet.
  const grouped = new Map();
  if (catalogue) {
    for (const { region, territories: terrs } of catalogue) {
      const covered = terrs.filter(t => territories.includes(t));
      if (covered.length === 0) continue;
      grouped.set(region, { covered, total: terrs.length });
    }
  } else {
    grouped.set('Coverage', { covered: territories, total: territories.length });
  }

  const regionCount = grouped.size;
  const territoryCount = territories.length;
  // Show ≤ 3 regions collapsed with the rest hidden behind "+N more".
  const entries = Array.from(grouped.entries());
  const visible = expanded ? entries : entries.slice(0, 3);
  const overflow = entries.length - visible.length;

  return (
    <Card className="mb-4" data-testid="coverage-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-emerald-600" />
            <h3 className="font-bold text-gray-900 text-sm">Lead Coverage Area</h3>
            <span className="text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
              {territoryCount} territor{territoryCount === 1 ? 'y' : 'ies'} · {regionCount} region{regionCount === 1 ? '' : 's'}
            </span>
          </div>
          {entries.length > 3 && (
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              data-testid="coverage-card-toggle"
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
            >
              {expanded ? 'Show less' : `Show all ${entries.length}`}
            </button>
          )}
        </div>
        <div className="space-y-2">
          {visible.map(([region, { covered, total }]) => {
            const full = total > 0 && covered.length === total;
            return (
              <div key={region} data-testid={`coverage-region-${region}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-gray-800">{region}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${full ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                    {full ? 'entire region' : `${covered.length} / ${total || covered.length}`}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {covered.map(t => (
                    <span
                      key={t}
                      className="inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-200"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
          {!expanded && overflow > 0 && (
            <div className="text-xs text-gray-500 italic">
              +{overflow} more region{overflow === 1 ? '' : 's'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
